import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import ButtonLink from '../../../../common/components/ButtonLink';
import { MessageType } from '../../../../common/components/ErrorList';
import HR from '../HorizontalRule';
import Redirect from '../../../../common/components/Redirect';
import {
  TitleBar,
  DocTitle,
} from '../../../../common/components/Layout/StandardPageTemplate/Context';
import { withLoading } from '../../../../redux-modules/loading-overlay';
import composes from '../../../../common/utils/composes';
import {
  SortProperty,
  genericObjectSort,
} from '../../../../common/utils/object-sort';
import {
  MembershipType,
  MembershipStatus,
} from '../../../../common/option-lists';
import SurveyActions from '../../actions/surveys';
import { getSurveys } from '../../selectors/surveys';
import ActivityList from '../ActivitiesPage/ActivityList';
import ActionHandler from '../ActionHandler';
import LoadingView from '../../../../common/components/LoadingView';
import styles from './ListPage.css';

class SurveyListPage extends React.Component {
  static propTypes = {
    surveys: PropTypes.arrayOf(PropTypes.object),
    currentUser: PropTypes.shape({
      type: PropTypes.string,
    }),
    currentCustomer: PropTypes.shape({
      organization: PropTypes.shape({
        key: PropTypes.string,
      }),
    }),
    currentOrganization: PropTypes.shape({
      membershipDetails: PropTypes.shape({
        type: PropTypes.string,
        statusType: PropTypes.string,
      }),
    }),
    isLoading: PropTypes.bool,
    actions: PropTypes.shape({
      surveys: PropTypes.objectOf(PropTypes.func),
    }),
  };
  static promises = {
    includePromises: [
      { type: 'ENTITY/ENTITY_GET', meta: { namespace: 'surveys' } },
    ],
  };
  static listType = {
    todo: 'todo',
    completed: 'completed',
  };
  state = {
    redirect: null,
    joinRenewMessage: [],
    joinRenewAction: null,
  };

  constructor(props) {
    super(props);

    // handle messaging if user came from landing page
    this.state = {
      redirect: null,
      isFetchingSurveys: !props.surveys || !props.surveys.length,
      ...this.determineJoinRenewMessaging(props),
    };
  }

  fetchSurveys = (...args) => {
    this.setState({ isFetchingSurveys: true });
    return this.props.actions.surveys
      .get(...args)
      .then(result => {
        this.setState({ isFetchingSurveys: false });
        return result;
      })
      .catch(error => {
        this.setState({ isFetchingSurveys: false });
        throw error;
      });
  };

  componentWillReceiveProps(nextProps) {
    // handle messaging on refresh
    const { currentOrganization, currentCustomer } = this.props;
    const nextOrganization = nextProps.currentOrganization;
    const nextCustomer = nextProps.currentCustomer;

    if (!currentCustomer && nextCustomer) {
      if (!nextCustomer.organization || !nextCustomer.organization.key) {
        // users with no associated org should be prompted to join
        return this.setState(prevState => ({
          ...prevState,
          joinRenewMessage: [
            'Our members drive policy through our ballots.' +
              ' Join today to use your voice at the state and federal level.',
          ],
          joinRenewAction: 'Join',
        }));
      }
    }

    // if this is the first time we have an organization
    if (!currentOrganization && nextOrganization) {
      const messaging = this.determineJoinRenewMessaging(nextProps);
      return this.setState(prevState => ({
        ...prevState,
        ...messaging,
      }));
    }
  }

  determineJoinRenewMessaging = props => {
    // determine join renew messaging
    const { joinRenewMessage, joinRenewAction } = this.state;
    const { currentUser, currentOrganization, currentCustomer } = props;

    if (!currentCustomer) {
      return null;
    }

    const { membershipDetails } = currentOrganization || {};
    const { type: userType } = currentUser || {};

    let message = joinRenewMessage || [];
    let action = joinRenewAction || null;

    if (
      userType === 'PrimaryContact' &&
      membershipDetails &&
      membershipDetails.type === MembershipType.Member &&
      membershipDetails.statusType === MembershipStatus.Lapsed
    ) {
      // inactive members should be asked to renew
      action = 'Renew';
    }

    if (
      userType !== 'SecondaryContact' &&
      (!membershipDetails ||
        membershipDetails.type === MembershipType.None ||
        membershipDetails.statusType === MembershipStatus.None)
    ) {
      // non members should be asked to join
      action = 'Join';
    }

    if (action) {
      message = [
        'Our members drive policy through our ballots.' +
          ` ${action} today to use your voice at the state and federal level.`,
      ];
    }

    return {
      joinRenewMessage: message,
      joinRenewAction: action,
    };
  };

  handleSurveyOnClick = survey => () => {
    const { key: surveyId } = survey;
    this.setState(prevState => ({
      ...prevState,
      redirect: `/account/surveys/${surveyId}`,
    }));
  };

  handleActionDismiss = () => {
    this.setState(prevState => ({
      ...prevState,
      joinRenewMessage: [],
      joinRenewAction: null,
    }));
  };

  handleActionConfirm = () => {
    const { joinRenewAction, redirect } = this.state;
    const { currentUser } = this.props;
    if (joinRenewAction) {
      let newRedirect = redirect;
      switch (joinRenewAction) {
        case 'Join':
          newRedirect = `/redirect/join?cst=${currentUser.id}`;
          break;

        case 'Renew':
          newRedirect = `/redirect/renew?cst=${currentUser.id}`;
          break;

        default:
          break;
      }
      this.setState(prevState => ({
        ...prevState,
        redirect: newRedirect,
      }));
    }
  };

  convertSurveyToTile = survey => {
    const { legislatureType, type, key, endDateTime, name } = survey;
    let color = 'grey';
    if (legislatureType.toLowerCase() === 'state') {
      color = 'orange';
    }
    if (legislatureType.toLowerCase() === 'national') {
      color = 'blue';
    }
    const action = !survey.isAnswered
      ? {
          action: {
            label: 'view',
            icon: 'chevron_right',
            clickHandler: this.handleSurveyOnClick(survey),
          },
        }
      : {};
    return {
      id: key,
      color,
      icon:
        type.toLowerCase() === 'ballot'
          ? 'format_list_bulleted'
          : 'content_paste',
      details: {
        openThru: {
          displayName: 'open through',
          value: moment(endDateTime).format('M/D/YYYY'),
          className: styles.openThruDate,
        },
        type: {
          value: type,
        },
        name: {
          value: name,
          className: styles.surveyName,
        },
      },
      ...action,
    };
  };

  getSortProps = ({ dateAscending }) => {
    return [
      new SortProperty('type'),
      new SortProperty('legislatureType'),
      new SortProperty('endDateTime', dateAscending),
    ];
  };

  getMessageForNoData = listType => {
    const persona = this.props.currentUser.type || '';
    const orgDetails =
      (this.props.currentOrganization || {}).membershipDetails || {};

    let itemType = 'surveys';

    // if member primary
    if (
      persona.toLowerCase() === 'primaryContact'.toLowerCase() &&
      orgDetails.type === MembershipType.Member
    ) {
      if (
        orgDetails.statusType === MembershipStatus.Active ||
        listType === SurveyListPage.listType.completed
      ) {
        // active members will have todo and completed ballots
        // but lapsed members will only have completed ballots
        itemType = 'ballots or surveys';
      }
    }

    const message =
      listType === SurveyListPage.listType.todo
        ? `No ${itemType} requiring your attention at this time.`
        : `No completed ${itemType} on file.`;

    return message;
  };

  render() {
    // check if we should redirect
    const { redirect, joinRenewMessage, joinRenewAction } = this.state;
    if (redirect) {
      return <Redirect external to={redirect} />;
    }

    const orgDetails =
      (this.props.currentOrganization || {}).membershipDetails || {};
    const surveys = this.props.surveys || [];
    const isMember =
      orgDetails.type === MembershipType.Member &&
      orgDetails.statusType === MembershipStatus.Active;

    // split up and sort surveys
    const todoSort = this.getSortProps({ dateAscending: true });
    const doneSort = this.getSortProps({ dateAscending: false });
    const todo = surveys
      .filter(
        survey =>
          !survey.isAnswered &&
          (isMember || survey.type.toLowerCase() === 'survey') &&
          survey.type.toLowerCase() !== 'microsurvey'
      )
      .sort(genericObjectSort(todoSort))
      .map(this.convertSurveyToTile);
    const done = surveys
      .filter(survey => survey.isAnswered)
      .sort(genericObjectSort(doneSort))
      .map(this.convertSurveyToTile);

    const pageTitle = 'My Ballots & Surveys';
    const pageColor = 'grey';

    const listProps = {
      color: pageColor,
      isLoading: this.props.isLoading,
    };

    const statusMessageProps = {
      type: MessageType.warning,
      errors: joinRenewMessage,
      actions: [
        {
          raised: true,
          label: 'Not Now',
          onClick: this.handleActionDismiss,
          className: styles.action,
        },
        {
          raised: true,
          label: joinRenewAction,
          onClick: this.handleActionConfirm,
          className: styles.action,
        },
      ],
      container: {
        className: styles.messageListContainer,
      },
      actionContainer: {
        className: styles.messageActions,
      },
    };

    return (
      <div>
        <TitleBar title={pageTitle} />
        <DocTitle title={pageTitle} />
        <ActionHandler
          condition={!this.props.surveys || !this.props.surveys.length}
          action={this.fetchSurveys}
          name="get-surveys"
        />
        {this.state.isFetchingSurveys ? (
          <LoadingView />
        ) : (
          <div>
            <ActivityList
              title="to do"
              activities={todo}
              noDataMessage={this.getMessageForNoData(
                SurveyListPage.listType.todo
              )}
              statusMessage={statusMessageProps}
              {...listProps}
            />
            <HR color={pageColor} />
            <ActivityList
              title="completed"
              activities={done}
              noDataMessage={this.getMessageForNoData(
                SurveyListPage.listType.completed
              )}
              {...listProps}
            />
          </div>
        )}
        <HR color={pageColor} />
        <ButtonLink
          primary
          raised
          to="/account"
          className={styles.buttonLink}
          label="Back to Account Management"
          style={{ marginTop: 0 }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    surveys: getSurveys(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: {
      surveys: bindActionCreators(SurveyActions, dispatch),
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(
  composes(SurveyListPage, withLoading)
);



// WEBPACK FOOTER //
// ./src/App/account/components/Surveys/ListPage.js